import { type DependencyList, type RefObject,useEffect, useRef } from "react";

import { prefersReducedMotion } from "./anime";

/**
 * Memoised `getBoundingClientRect`, the plumbing under every cursor tracker.
 * The box is measured lazily and re-measured only after resize/scroll: reading
 * the rect inside a pointer handler would force a synchronous layout on every
 * move. `dispose` detaches the invalidation listeners.
 */
export function watchRect(el: Element) {
  let box: DOMRect | null = null;
  const measure = () => (box = el.getBoundingClientRect());
  const invalidate = () => {
    box = null;
  };
  window.addEventListener("resize", invalidate);
  window.addEventListener("scroll", invalidate, { passive: true });
  return {
    read: () => box ?? measure(),
    measure,
    invalidate,
    dispose: () => {
      window.removeEventListener("resize", invalidate);
      window.removeEventListener("scroll", invalidate);
    },
  };
}

/**
 * Shared plumbing for cursor-reactive effects: an rAF-throttled `mousemove`
 * that hands `apply` the pointer position normalised to the element (0…1),
 * and calls `reset` when the cursor leaves. No-op on touch / reduced-motion.
 */
function usePointerEffect<T extends HTMLElement>(
  apply: (el: T, x: number, y: number) => void,
  reset?: (el: T) => void,
  deps: DependencyList = [],
): RefObject<T | null> {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;
    if (typeof window === "undefined" || window.matchMedia("(hover: none)").matches) return;

    let raf = 0;
    // rect memoised via `watchRect`, re-measured on enter, /companies carries
    // five of these cards, so per-event measuring would thrash layout
    const rect = watchRect(el);

    const onEnter = () => rect.measure();
    const onMove = (e: MouseEvent) => {
      const r = rect.read();
      if (!r.width || !r.height) return;
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => apply(el, x, y));
    };
    const onLeave = () => {
      cancelAnimationFrame(raf);
      rect.invalidate();
      reset?.(el);
    };
    el.addEventListener("mouseenter", onEnter, { passive: true });
    el.addEventListener("mousemove", onMove, { passive: true });
    el.addEventListener("mouseleave", onLeave, { passive: true });
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      rect.dispose();
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}

/** Subtle 3D tilt-on-hover. Attach the returned ref to a card. No-op on touch / reduced-motion. */
export function useTilt<T extends HTMLElement = HTMLDivElement>(max: number): RefObject<T | null> {
  return usePointerEffect<T>(
    (el, x, y) => {
      const rx = (-(y - 0.5) * max).toFixed(2);
      const ry = ((x - 0.5) * max).toFixed(2);
      el.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg)`;
    },
    (el) => {
      el.style.transform = "perspective(800px) rotateX(0deg) rotateY(0deg)";
    },
    [max],
  );
}

/**
 * Publishes the cursor position inside the element as `--mx` / `--my`
 * percentages, for CSS to point a gradient/spotlight at.
 */
export function useSpotlight<T extends HTMLElement = HTMLDivElement>(): RefObject<T | null> {
  return usePointerEffect<T>((el, x, y) => {
    el.style.setProperty("--mx", `${(x * 100).toFixed(1)}%`);
    el.style.setProperty("--my", `${(y * 100).toFixed(1)}%`);
  });
}
